import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef } from 'react';

import { registerDebounced, unregisterDebounced } from '../utils/debounceRegistry';

/** Options for {@link useDebouncedUpdate}. */
export interface UseDebouncedUpdateOptions {
  /** Delay in ms before invoking the update function. */
  delay?: number;
  /** Invoke on the leading edge instead of the trailing edge. */
  immediate?: boolean;
  /** Maximum wait time in ms before forcing an update. */
  maxWait?: number;
  /** Optional error handler when the update function throws. */
  onError?: (error: Error) => void;
}

/**
 * Custom hook that provides a debounced update function
 * Useful for text inputs to reduce the number of state updates
 *
 * @param updateFn - The function to debounce
 * @param delay - Debounce delay in milliseconds (default: 300ms)
 * @returns Debounced version of the update function
 */
export function useDebouncedUpdate<T extends (...args: any[]) => any>(
  updateFn: T,
  delayOrOptions: number | UseDebouncedUpdateOptions = 300
): T & { flush: () => void; cancel: () => void } {
  const options = typeof delayOrOptions === 'number' ? { delay: delayOrOptions } : delayOrOptions;

  const { delay = 300, immediate = false, maxWait = 1000, onError } = options;

  const debouncedFnRef = useRef(
    debounce(
      async (...args: Parameters<T>) => {
        try {
          await updateFn(...args);
        } catch (err) {
          if (onError) onError(err as Error);
        }
      },
      delay,
      { leading: immediate, trailing: true, maxWait }
    )
  );

  // Register this debounced function globally so it can be flushed
  useEffect(() => {
    const entry = { flush: () => debouncedFnRef.current.flush() };
    registerDebounced(entry);
    return () => {
      unregisterDebounced(entry);
    };
  }, []);

  // Update the debounced function if options change
  useEffect(() => {
    debouncedFnRef.current = debounce(
      async (...args: Parameters<T>) => {
        try {
          await updateFn(...args);
        } catch (err) {
          if (onError) onError(err as Error);
        }
      },
      delay,
      { leading: immediate, trailing: true, maxWait }
    );

    // Cleanup to flush and cancel pending invocation when parameters change
    return () => {
      debouncedFnRef.current.flush();
      debouncedFnRef.current.cancel();
    };
  }, [updateFn, delay, immediate, maxWait, onError]);

  const debouncedCallback = useCallback((...args: Parameters<T>) => {
    debouncedFnRef.current(...args);
  }, []);

  (debouncedCallback as any).flush = () => debouncedFnRef.current.flush();
  (debouncedCallback as any).cancel = () => debouncedFnRef.current.cancel();

  return debouncedCallback as T & { flush: () => void; cancel: () => void };
}

/**
 * Hook specifically for debouncing text input updates
 * Provides immediate local state updates with debounced external updates
 */
export function useDebouncedTextInput(
  value: string,
  onChange: (value: string) => void,
  delay: number = 300
) {
  const debouncedOnChange = useDebouncedUpdate(onChange, delay);

  const handleChange = useCallback(
    (newValue: string) => {
      // Call the debounced function
      debouncedOnChange(newValue);
    },
    [debouncedOnChange]
  );

  const handleBlur = useCallback(() => {
    debouncedOnChange.flush();
  }, [debouncedOnChange]);

  return {
    value,
    onChange: handleChange,
    onBlur: handleBlur,
  };
}
